import BackButton from "@/components/BackButton";
import { Link } from "react-router-dom";
import {
  Music, PlayCircle, Target, Mic2, BookOpen, Sparkles,
  Pencil, FileSignature, CreditCard,
} from "lucide-react";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="border border-border rounded-2xl p-6 md:p-8 bg-card grain-panel" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-primary/40 text-primary">
          <Icon size={18} />
        </span>
        <h2 className="font-display text-2xl md:text-3xl tracking-tight">{title}</h2>
      </div>
      <div className="mt-4 space-y-3 text-foreground/85 leading-relaxed">{children}</div>
    </section>
  );
}

export default function HowItWorks() {
  return (
    <main data-testid="how-it-works-page" className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
      <BackButton to="/" label="Back to home" testid="how-back-link" />

      <header className="mt-8">
        <p className="overline-label text-accent">Guide</p>
        <h1 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
          How it works
        </h1>
        <p className="mt-4 text-lg text-muted-foreground max-w-xl">
          A quick tour so you can walk on stage knowing every word. Five minutes.
        </p>
      </header>

      <div className="mt-12 space-y-6">
        <Section icon={Pencil} title="1. Add a song" testid="how-add-song">
          <p>
            From your library, tap <span className="text-foreground font-medium">Add a song</span>.
            Search a public lyrics database by artist and title, or paste your own lyrics directly.
          </p>
          <p>
            Lyrics are split into sections automatically — verse, chorus, bridge — using the headings
            in brackets like <span className="font-mono text-xs">[Chorus]</span>, or by blank lines.
            You can edit any song later with the pencil icon.
          </p>
        </Section>

        <Section icon={PlayCircle} title="2. The three stages: Play, Test, Perform" testid="how-stages">
          <p>
            Every song moves through the same arc. Pick the stage from the switcher at the top of the
            practice page.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="text-foreground font-medium">Play</span> — your rehearsal room. Pick a
              drill, get hints, slow it down, no pressure. This is where learning happens.
            </li>
            <li>
              <span className="text-foreground font-medium">Test</span> — grade yourself. No hints,
              one final score, honest feedback on where to shore up.
            </li>
            <li>
              <span className="text-foreground font-medium">Perform</span> — the teleprompter. Big
              lyrics, backing track, nothing in the way. Use this on stage or in the mirror.
            </li>
          </ul>
        </Section>

        <Section icon={Target} title="3. The five practice drills" testid="how-drills">
          <p>Inside <span className="text-foreground font-medium">Play</span>, pick the drill that fits what you need right now.</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="text-foreground font-medium">Blanks</span> — words drop out of the
              lyrics and you type them back in. Difficulty scales as you improve.
            </li>
            <li>
              <span className="text-foreground font-medium">Line by line</span> — one line at a time.
              You read it, flip it, say it from memory, then move on.
            </li>
            <li>
              <span className="text-foreground font-medium">First letter</span> — only the first
              letter of every word is shown. A classic cold-start cue.
            </li>
            <li>
              <span className="text-foreground font-medium">Section recall</span> — the whole section
              is hidden; you type (or speak) it from scratch, then compare.
            </li>
            <li>
              <span className="text-foreground font-medium">Sing it</span> — the voice drill. Hit
              record, sing the section, and the app transcribes and grades what came out of your mouth.
            </li>
          </ul>
        </Section>

        <Section icon={Sparkles} title="4. Small-talk notes about the song" testid="how-notes">
          <p>
            Open any song from your library and scroll to the{" "}
            <span className="text-foreground font-medium">Memory Map</span> panel just above the
            lyrics. That's where the AI drops friendly context — meaning, mood, story cues, and small
            talk you can share with a crowd between verses. Tap{" "}
            <span className="text-foreground font-medium">Analyze</span> to generate it, and it stays
            saved with the song.
          </p>
        </Section>

        <Section icon={Mic2} title="5. Backing tracks" testid="how-tracks">
          <p>
            On any song page you can upload an MP3 backing track. It plays inside{" "}
            <span className="text-foreground font-medium">Perform</span> and{" "}
            <span className="text-foreground font-medium">Sing it</span> so you can rehearse to the
            real tempo and key.
          </p>
        </Section>

        <Section icon={BookOpen} title="6. Free vs. Pro" testid="how-plans">
          <p>Both plans share every practice mode and every drill. The only difference is library size.</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="text-foreground font-medium">Free</span> — save up to{" "}
              <span className="text-foreground font-medium">3 songs</span>. Enough to try every drill
              and see if the app earns a spot in your routine.
            </li>
            <li>
              <span className="text-foreground font-medium">Pro</span> — unlimited songs, your whole
              setlist, forever. One flat monthly price.
            </li>
          </ul>
          <p>
            Upgrade any time from the{" "}
            <Link to="/billing" className="underline hover:text-primary" data-testid="how-billing-link">
              Billing page
            </Link>
            .
          </p>
        </Section>

        <Section icon={CreditCard} title="7. Managing or canceling your subscription" testid="how-manage">
          <p>
            After you upgrade, the Billing page shows a{" "}
            <span className="text-foreground font-medium">Manage subscription</span> button. That
            opens a secure Stripe portal where you can:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Update your card.</li>
            <li>Download past receipts.</li>
            <li>
              Cancel. Your Pro features stay on until the end of the current billing period — no
              surprise cut-off mid-month. After that, your library stays intact; you just drop back
              to the 3-song Free limit until you resubscribe.
            </li>
          </ul>
        </Section>

        <Section icon={FileSignature} title="8. Anything else?" testid="how-contact">
          <p>
            Lost? Confused? Have a feature idea? Say hello on the{" "}
            <Link to="/contact" className="underline hover:text-primary" data-testid="how-contact-link">
              Contact page
            </Link>
            . A real human reads every note.
          </p>
        </Section>
      </div>

      <div className="mt-14 flex items-center gap-3 flex-wrap">
        <Link to="/library" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-primary text-primary-foreground glow-amber-sm hover:glow-amber transition-shadow text-sm font-medium"
              data-testid="how-cta-library">
          <Music size={16} /> Go to my library
        </Link>
        <Link to="/billing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="how-cta-billing">
          See plans
        </Link>
      </div>
    </main>
  );
}
